import React, { memo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { KNOWLEDGE_DOCUMENT_ACCEPT } from '@lgg/isomorphic/knowledge-document-file';
import {
  AiAgentDocumentSourceItem,
  AiAgentDocumentSourceItemOrderByInput,
  Query,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { TablePagination } from 'src/components/general/display/table-pagination';
import { TableBodyPlaceholder } from 'src/components/general/feedback/loading-placeholders';
import { AttachmentGhostInput } from 'src/components/general/inputs/ghost-input';
import {
  KnowledgeSourceContainer,
  KnowledgeSourceEmptyState,
  KnowledgeSourceHeader,
} from 'src/components/pages/ai-agents/components/knowledge-tabs';
import { AiAgentsListVariables } from 'src/components/pages/ai-agents/hooks/use-ai-agents-list-query-params';
import { useKnowledgeSourceListState } from 'src/components/pages/ai-agents/hooks/use-knowledge-source-list-state';
import { DocumentSourcesTable } from 'src/components/pages/ai-agents/knowledge-base-sources/documents/components/document-sources-table';
import { useDeleteDocumentSourceItem } from 'src/components/pages/ai-agents/knowledge-base-sources/documents/hooks/use-delete-document-source-item';
import { useUploadDocuments } from 'src/components/pages/ai-agents/knowledge-base-sources/documents/hooks/use-upload-documents';
import { DOCUMENT_SOURCE_ITEMS_LIST_QUERY } from 'src/components/pages/ai-agents/knowledge-base-sources/documents/queries';
import { useAuthorization } from 'src/hooks/use-authorization';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { useHandleGraphQLError } from 'src/hooks/use-handle-graphql-error';

type DocumentSourcesListVariables =
  AiAgentsListVariables<AiAgentDocumentSourceItemOrderByInput> & {
    knowledgeBaseId: string;
  };

export const DocumentSources = memo<{ knowledgeBaseId: string; aiAgentId?: string }>(
  ({ knowledgeBaseId, aiAgentId }) => {
    const { t } = useTranslation(['aiAgents']);
    const { id: companyId } = useCurrentInstitution();
    const { hasPermission } = useAuthorization();
    const handleGraphQLError = useHandleGraphQLError();
    const canCreate = hasPermission('ai:company.ai_agent_knowledge_base:create');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { sortData, listVariables, onTableChange, getPaginationProps } =
      useKnowledgeSourceListState<AiAgentDocumentSourceItem>();

    const variables: DocumentSourcesListVariables = {
      companyId,
      knowledgeBaseId,
      ...listVariables,
    };

    const { data, loading, refetch } = useQuery<
      Pick<Query, 'aiAgents'>,
      DocumentSourcesListVariables
    >(DOCUMENT_SOURCE_ITEMS_LIST_QUERY, { variables, onError: handleGraphQLError });

    const { uploadDocuments, isUploading } = useUploadDocuments({
      knowledgeBaseId,
      onUploaded: () => void refetch(),
    });

    const openDelete = useDeleteDocumentSourceItem({
      aiAgentId,
      onDeleted: () => void refetch(),
    });

    if (loading) {
      return <TableBodyPlaceholder />;
    }

    if (!data) {
      return null;
    }

    const { nodes, totalCount, pageInfo } = data.aiAgents.listDocumentSourceItems;
    const openFileBrowser = () => fileInputRef.current?.click();

    return (
      <KnowledgeSourceContainer data-lgg-id="document-sources">
        {canCreate && (
          <AttachmentGhostInput
            ref={fileInputRef}
            type="file"
            multiple
            accept={KNOWLEDGE_DOCUMENT_ACCEPT}
            data-lgg-id="document-sources-file-input"
            onChange={(event) => {
              const files = Array.from(event.target.files ?? []);
              event.target.value = '';
              void uploadDocuments(files);
            }}
          />
        )}
        {totalCount === 0 ? (
          <KnowledgeSourceEmptyState
            sourceKey="documents"
            canAdd={canCreate}
            onAdd={isUploading ? undefined : openFileBrowser}
          />
        ) : (
          <>
            <KnowledgeSourceHeader
              testId="document-sources"
              count={totalCount}
              countLabel={t('aiAgents:knowledge.documents.count', { count: totalCount })}
              addLabel={t('aiAgents:knowledge.documents.addButton')}
              addDisabled={isUploading}
              onAdd={canCreate ? openFileBrowser : undefined}
            />
            <DocumentSourcesTable
              data={nodes}
              sortData={sortData}
              onDelete={openDelete}
              onChange={onTableChange}
            />
            {(pageInfo.hasPreviousPage || pageInfo.hasNextPage) && (
              <TablePagination {...getPaginationProps(pageInfo)} />
            )}
          </>
        )}
      </KnowledgeSourceContainer>
    );
  },
);
